import React, { useEffect, useState } from 'react';
import { navigate } from 'gatsby-link';
import { Select, SelectOption } from '@landr/maestro';
import { withAppContext, AppContextType } from 'context/AppContext';
import { getTwoLetterLanguageCode } from 'helpers/getTwoLetterLanguageCode';
import { languagesListCopyMapping, AlternateLanguage, PrismicLocales } from 'types';
import { isHomePage } from 'helpers';

const makeUrl = (lang: string, uid: string) => `/${getTwoLetterLanguageCode(lang)}/${uid}/`;
const makeAHomeUrl = (lang: string) => `/${getTwoLetterLanguageCode(lang)}/`;
const getLanguageCopy = (lang: string): string => languagesListCopyMapping[getTwoLetterLanguageCode(lang)];

const createSelectData = (currentUid: string, alternateLanguages: AlternateLanguage[]): SelectOption[] =>
    alternateLanguages.map((alternateLanguage) => {
        const value = isHomePage(currentUid)
            ? makeAHomeUrl(alternateLanguage.lang)
            : makeUrl(alternateLanguage.lang, alternateLanguage.uid);
        const text = getLanguageCopy(alternateLanguage.lang);
        return { value, text, key: alternateLanguage.lang };
    });

const LanguageSelector = withAppContext(
    ({
        context: {
            pageContext: { uid: currentUid, lang },
            alternateLanguages,
        },
    }: {
        context: AppContextType;
    }) => {
        const currentLanguageCopy = getLanguageCopy(lang);
        const [options, setOptions] = useState<SelectOption[]>();

        useEffect(() => {
            const data = createSelectData(currentUid, alternateLanguages);

            const unsortedOptions = [
                {
                    key: lang,
                    text: currentLanguageCopy,
                    value: lang,
                },
                ...data,
            ];

            // Sort by language based on PrismicLocales order
            const sortedOptions = unsortedOptions.sort(
                (a, b) => PrismicLocales.indexOf(a.key) - PrismicLocales.indexOf(b.key),
            );
            setOptions(sortedOptions);
        }, [currentUid, lang, alternateLanguages, currentLanguageCopy]);

        if (!options) {
            return null;
        }
        return (
            <Select
                aria-label="language selector"
                defaultValue={lang}
                onChange={(event) => navigate(event.value as string)}
                options={options}
            />
        );
    },
);

export { LanguageSelector };
